import { useContext, useEffect, useState } from "react";
import {
  Audience,
  AudienceOutline,
  Automations,
  AutomationsOutline,
  Calendar,
  Campaigns,
  CampaignsOutline,
  Dashboard,
  DashboardOutline,
  Forms,
  Path,
} from "@tiny/icons";
import { Chip, Theme } from "@mui/material";
import { RouteKey } from "@/providers/types";
import { ProviderConfigContext } from "@/providers/ProviderConfigContext";
import { isAllowed } from "@/providers/utils";
import { useJwtToken } from "../../hooks/useJwtToken";
import { MenuItem } from "./types";

type MenuItemWithRouteKey = MenuItem & { routeKey: RouteKey };

const SecondaryLabel = () => (
  <Chip
    sx={(theme: Theme) => ({
      height: theme.spacing(6),
      backgroundColor: theme.palette.grey[30],
      color: theme.palette.grey[100],
      mr: 1,
      fontSize: theme.spacing(3),
      lineHeight: theme.spacing(4),
      "& .MuiChip-label": { fontWeight: 400 },
    })}
    label="Beta"
  />
);

const baseMenuItems: MenuItemWithRouteKey[] = [
  {
    name: "Dashboard",
    tooltip: "Dashboard",
    to: "dashboard/overview",
    Icon: DashboardOutline,
    IconActive: Dashboard,
    webOnly: false,
    exact: true,
    routeKey: "dashboard",
  },
  {
    name: "Automations",
    tooltip: "Automations",
    to: "automations/overview",
    Icon: AutomationsOutline,
    IconActive: Automations,
    webOnly: false,
    exact: false,
    routeKey: "automations",
  },
  {
    name: "Campaigns",
    tooltip: "Campaigns",
    to: "campaigns/overview/future-campaigns",
    Icon: CampaignsOutline,
    IconActive: Campaigns,
    webOnly: false,
    exact: false,
    routeKey: "campaigns",
  },
  {
    name: "Calendar",
    tooltip: "Campaigns",
    to: "campaigns/calendar",
    Icon: Calendar,
    IconActive: Calendar,
    webOnly: false,
    exact: true,
    routeKey: "calendar",
  },
  {
    name: "Forms",
    tooltip: "Forms",
    to: "forms",
    Icon: Forms,
    IconActive: Forms,
    webOnly: false,
    exact: false,
    routeKey: "forms",
  },
  {
    name: "Audience",
    tooltip: "Audience",
    to: "audience",
    Icon: AudienceOutline,
    IconActive: Audience,
    webOnly: false,
    exact: false,
    routeKey: "audience",
  },
];

const workflowsMenuItem: MenuItemWithRouteKey = {
  name: "Workflows",
  tooltip: "Workflows",
  Icon: Path,
  IconActive: Path,
  to: "workflows",
  webOnly: false,
  exact: false,
  routeKey: "workflows",
};

export const useMainMenu = (): MenuItem[] => {
  const { allowedRoutes } = useContext(ProviderConfigContext);
  const { token } = useJwtToken();
  const [allMenuItems, setAllMenuItems] =
    useState<MenuItemWithRouteKey[]>(baseMenuItems);

  useEffect(() => {
    setAllMenuItems([...baseMenuItems, workflowsMenuItem]);
  }, [token]);

  return allMenuItems.filter((item) => isAllowed(item.routeKey, allowedRoutes));
};
